import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { ArrowLeft, Link2 } from "lucide-react";
import { FaLinkedin as Linkedin, FaTwitter as Twitter } from "react-icons/fa";
import PageShell, { SectionLabel } from "@/components/PageShell";
import { getBlogPost } from "@/lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getBlogPost(slug)
      .then(setPost)
      .catch((e) => setError(e.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const share = (kind) => {
    if (!post) return;
    if (kind === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
      return;
    }
    const encTitle = encodeURIComponent(post.title);
    const encUrl = encodeURIComponent(url);
    const dest =
      kind === "x"
        ? `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`
        : `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`;
    window.open(dest, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <PageShell testId="page-blog-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <p data-testid="blog-detail-loading" className="font-mono text-sm text-zinc-500">
            Loading article…
          </p>
        </div>
      </PageShell>
    );
  }

  if (error === "not_found" || !post) {
    return (
      <PageShell testId="page-blog-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] mb-4">
            404
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-white mb-8">
            This article couldn&apos;t be found.
          </h1>
          <Link
            to="/blog"
            data-testid="blog-detail-back-404"
            className="inline-flex items-center gap-2 text-white border border-white/10 hover:border-[#CCFF00]/50 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            <ArrowLeft size={14} /> Back to insights
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell testId="page-blog-detail">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="pt-16 md:pt-24">
          <Link
            to="/blog"
            data-testid="blog-detail-back"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#CCFF00] transition-colors"
          >
            <ArrowLeft size={12} /> Back to insights
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pt-8 pb-12">
          <div className="md:col-span-4">
            <SectionLabel index={1}>{post.category}</SectionLabel>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-8">
              Written by
            </p>
            <p className="font-display text-lg text-white mt-1">{post.author}</p>
            <p className="font-mono text-[10px] text-zinc-500 mt-6">
              {post.read_time} · {new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-8"
          >
            <h1
              data-testid="blog-detail-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
            >
              {post.title}
            </h1>
            <p className="mt-6 text-xl text-zinc-400 leading-relaxed">{post.excerpt}</p>
          </motion.div>
        </div>

        <div className="border-t border-white/5">
          <div className="relative aspect-[16/7] overflow-hidden bg-[#050505] my-8">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-24">
          {/* Sticky share rail */}
          <aside className="md:col-span-2">
            <div className="sticky top-24 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Share
              </p>
              <div className="flex md:flex-col gap-2">
                <button
                  onClick={() => share("x")}
                  data-testid="blog-share-x"
                  aria-label="Share on X"
                  className="w-10 h-10 border border-white/10 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] text-zinc-400 flex items-center justify-center transition-colors"
                >
                  <Twitter size={14} />
                </button>
                <button
                  onClick={() => share("linkedin")}
                  data-testid="blog-share-linkedin"
                  aria-label="Share on LinkedIn"
                  className="w-10 h-10 border border-white/10 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] text-zinc-400 flex items-center justify-center transition-colors"
                >
                  <Linkedin size={14} />
                </button>
                <button
                  onClick={() => share("copy")}
                  data-testid="blog-share-copy"
                  aria-label="Copy link"
                  className="w-10 h-10 border border-white/10 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] text-zinc-400 flex items-center justify-center transition-colors"
                >
                  <Link2 size={14} />
                </button>
              </div>
            </div>
          </aside>

          <article
            data-testid="blog-detail-body"
            className="md:col-span-8 prose-invert max-w-none"
          >
            <div className="markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            </div>
          </article>

          <aside className="md:col-span-2 hidden md:block" />
        </div>

        <div className="border-t border-white/5 py-16 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <p className="font-display text-2xl text-white max-w-xl">
            Enjoyed this? We publish field notes when we have something worth reading.
          </p>
          <Link
            to="/contact"
            data-testid="blog-detail-cta"
            className="bg-[#CCFF00] text-black px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors"
          >
            Work with us
          </Link>
        </div>
      </div>
    </PageShell>
  );
}
